/**
 * HKSCA — **品牌單一來源**（`decisions/010` Phase A）
 *
 * ⚠️ 所有品牌字串／顏色／logo 一律由 `brand.json` 讀 —— **唔准喺其他地方硬編碼**。
 *    換協會 = 改 `brand.json`（＋換 3 個 logo PNG），**唔應該改到任何功能碼**。
 *
 * 用法：
 *   · 字串：`brandAppName(i18n.language)`／`brandTagline(lang)`／`brand.logos.mark`
 *   · 顏色：`applyBrand()`（喺 app 入口呼叫一次）→ 寫入 CSS var，覆蓋 `tokens.css` 嘅 fallback
 */
import raw from '../brand.json';

export interface BrandConfig {
  slug: string;
  nameZhHk: string;
  nameZhCn: string;
  nameEn: string;
  appNameZhHk: string;
  appNameZhCn: string;
  appNameEn: string;
  shortName: string;
  committeeAppNameZhHk: string;
  committeeAppNameZhCn: string;
  committeeAppNameEn: string;
  supportEmail: string;
  waNumber: string;
  memberNoPrefix: string;
  defaultLocale: string;
  colors: Record<string, string>;
  logos: { mark: string; horizontal: string; square: string };
  themeColor: string;
  backgroundColor: string;
}

/** ⚠️ 剔走註解欄位（`"//…"`）—— brand.json 用 `//key` 寫說明，唔屬設定值 */
const stripped = Object.fromEntries(Object.entries(raw as Record<string, unknown>).filter(([k]) => !k.startsWith('//')));

export const brand = stripped as unknown as BrandConfig;

export type Lang = 'zh-HK' | 'zh-CN' | 'en';

const pick = (hk: string, cn: string, en: string, lang?: string): string =>
  lang === 'en' ? en || hk : lang === 'zh-CN' ? cn || hk : hk || en;

/** App 顯示名（會員端；跟 UI 語言） */
export const brandAppName = (lang?: string): string =>
  pick(brand.appNameZhHk, brand.appNameZhCn, brand.appNameEn, lang);

/** 第 2 個 PWA（理事會端）顯示名 */
export const brandCommitteeName = (lang?: string): string =>
  pick(brand.committeeAppNameZhHk, brand.committeeAppNameZhCn, brand.committeeAppNameEn, lang);

/** 協會正式名 */
export const brandOrgName = (lang?: string): string => pick(brand.nameZhHk, brand.nameZhCn, brand.nameEn, lang);

/** 協會 tagline（正式名 ＋ 英文，用喺登入頁等） */
export const brandTagline = (lang?: string): string =>
  lang === 'en' ? brand.nameEn : lang === 'zh-CN' ? brand.nameZhCn : `${brand.nameZhHk} · ${brand.nameEn}`;

/** CSS var 對應（品牌色 → token 名） */
const VAR_OF: Record<string, string> = {
  primary: '--color-primary',
  primaryStrong: '--color-primary-strong',
  primarySoft: '--color-primary-soft',
  accent: '--color-accent',
  accentStrong: '--color-accent-strong',
  accentSoft: '--color-accent-soft',
  brandGradTo: '--color-brand-grad-to',
  bg: '--color-bg',
  bgTint: '--color-bg-tint',
  cardTop: '--color-card-top',
};

/**
 * 把 `brand.json` 嘅品牌色＋標題套入 DOM。
 * ⚠️ 喺**入口檔**（`main.tsx`／`committee-main.tsx`）呼叫一次；唔改任何功能行為，
 *    只係用 JS 覆寫 `tokens.css` 嘅 fallback（換品牌唔使改 CSS）。
 */
let lastOpts: { committee?: boolean } | undefined;

export function applyBrand(lang?: string, opts?: { committee?: boolean }): void {
  if (opts) lastOpts = opts;
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const [key, cssVar] of Object.entries(VAR_OF)) {
    const value = brand.colors?.[key];
    if (typeof value === 'string' && value.trim()) root.style.setProperty(cssVar, value.trim());
  }
  const title = lastOpts?.committee ? brandCommitteeName(lang) : brandAppName(lang);
  if (title) document.title = title;
}

/** 切換語言後重套品牌（標題跟語言）；由 `i18n.ts` 嘅 `changeLocale()` 呼叫 */
export function reapplyBrand(lang?: string): void {
  applyBrand(lang, lastOpts);
}
